// src/components/LandingPage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css'; // Import the enhanced CSS file
import { FaRocket, FaUsers, FaChartLine, FaRegLightbulb, FaRobot, FaArchive } from 'react-icons/fa'; // Import additional icons for features
import emailjs from '@emailjs/browser';

const LandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });

  const handleTryNow = () => {
    navigate('/login');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    try {
      // Utilizzo delle credenziali EmailJS fornite
      const result = await emailjs.send(
        'service_i1lv8sb', // Service ID
        'template_smmy4au', // Template ID
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_email: 'alberto.lino.masiero@gmail.com'
        },
        'DYcg_xKsnBk3TQk5q' // Public Key
      );

      if (result.status === 200) {
        setSubmitStatus({
          success: true,
          message: 'Thank you for your message! We will get back to you soon.'
        });
        // Reset form
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus({
          success: false,
          message: 'Failed to send message. Please try again later.'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({
        success: false,
        message: 'An error occurred. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-container">
      <video className="background-video" src='/landingpage-logo-video.mp4' autoPlay loop muted playsInline></video>
      {/* Hero Section */}
      <header className="hero-section">
        <div className="hero-overlay">
          <h1>AGEstimation Project</h1>
          <img src="/landing_tools.png" alt="Logo AGEstimation project" className="hero-image" />

          <p>
            Unlock precise age estimation through advanced radiographic analysis, statistical processing, and insightful scientific reports.
          </p>
          <button className="try-now-button" onClick={handleTryNow}>
            Try Now
          </button>
          <p>          <a href="https://ageestimation.netlify.app/privacy" target="_blank" rel="noopener noreferrer">
            Terms and Conditions of Use
          </a>{' '}</p>

        </div>
      </header>

      {/* Tools Showcase Section */}
      <section className="tools-section">
        <h2>Our Tools</h2>
        <p>
          Discover the powerful tools designed to provide precise age estimations across various radiographic applications.
        </p>
        <div className="tools-grid">
          {/* Tool 1 */}
          <div className="tool-card">
            <img src="/tools_i3m.webp" alt="Third Molar Maturity Index (I3M)" />
            <h3>Third Molar Maturity Index (I3M)</h3>
            <p>
              Accurately differentiate individuals above and below the legal adulthood age (18 years) using the maturity index of third molars.
            </p>
          </div>
          {/* Tool 2 */}
          <div className="tool-card">
            <img src="/tools_canini.webp" alt="Single-Rooted Teeth Age Estimation" />
            <h3>Single-Rooted Teeth Age Estimation</h3>
            <p>
              Utilize pulp-tooth area ratios from intraoral radiographs to estimate adult ages with high precision.
            </p>
          </div>
          {/* Tool 3 */}
          <div className="tool-card">
            <img src="/tools_carpal.webp" alt="Carpal Bones Area Age Estimation" />
            <h3>Carpal Bones Area Age Estimation</h3>
            <p>
              Implement area ratio analysis of carpal bones from X-rays to determine adult ages effectively.
            </p>
          </div>
          {/* Tool 4 */}
          <div className="tool-card">
            <img src="/tools_vertebra.webp" alt="Pubertal Growth Spurt Estimation" />
            <h3>Pubertal Growth Spurt Estimation</h3>
            <p>
              Assess pubertal growth spurts through vertebral body height projections for accurate developmental insights.
            </p>
          </div>
          {/* Tool 5 */}
          <div className="tool-card">
            <img src="/tools_europeanformula.webp" alt="Age Estimation in Children" />
            <h3>Age Estimation in Children</h3>
            <p>
              Measure open apices in teeth using the European formula to provide reliable age estimations in children.
            </p>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="ai-section">
        <div className="ai-container">
          <h2>Embracing the Future with AI</h2>
          <img src="/landingpage_ai.webp" alt="AI Integration in AGEstimation project" className="ai-image" />
          <p>
            We are integrating cutting-edge AI technologies to automate and simplify age estimation. Experience the future of radiographic analysis with our AI-powered tools.
          </p>
          <button className="secondary-button" onClick={handleTryNow}>
            Discover AI Integration
          </button>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2>Why Choose Our Platform?</h2>
        <div className="features-grid">
          {/* Feature 1 */}
          <div className="feature-card" data-aos="zoom-in">
            <FaRocket className="feature-icon" />
            <h3>Advanced Accuracy</h3>
            <p>
              Leveraging state-of-the-art image processing and statistical models to deliver unmatched accuracy in age estimation.
            </p>
          </div>
          {/* Feature 2 */}
          <div className="feature-card" data-aos="zoom-in" data-aos-delay="100">
            <FaUsers className="feature-icon" />
            <h3>User-Friendly Interface</h3>
            <p>
              Intuitive design ensures a seamless experience, allowing users to focus on analysis without technical hurdles.
            </p>
          </div>
          {/* Feature 3 */}
          <div className="feature-card" data-aos="zoom-in" data-aos-delay="200">
            <FaChartLine className="feature-icon" />
            <h3>Comprehensive Reporting</h3>
            <p>
              Generate detailed reports based on the latest scientific publications, providing valuable insights and data-driven conclusions.
            </p>
          </div>
          {/* Feature 4 */}
          <div className="feature-card" data-aos="zoom-in" data-aos-delay="300">
            <FaRegLightbulb className="feature-icon" />
            <h3>Continuous Innovation</h3>
            <p>
              We are committed to integrating pre-trained models and automating processes to enhance the platform's capabilities.
            </p>
          </div>
          {/* Feature 5: AI Integration */}
          <div className="feature-card" data-aos="zoom-in" data-aos-delay="400">
            <FaRobot className="feature-icon" />
            <h3>AI-Powered Analysis</h3>
            <p>
              Harness the power of artificial intelligence to automate and refine age estimation processes, setting the stage for the future of forensic analysis.
            </p>
          </div>
          {/* Feature 6: Resume Your Annotations */}
          <div className="feature-card" data-aos="zoom-in" data-aos-delay="500">
            <FaArchive className="feature-icon" />
            <h3>Resume Your Annotations</h3>
            <p>
              Seamlessly continue your annotation projects from our secure archive. Your progress is automatically saved, ensuring you never miss a step.
            </p>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="aboutus-section">
        <div className="aboutus-container">
          <h2>About Us</h2>
          <p>
            Our mission is to provide researchers, forensic experts, and dental professionals with the most reliable and efficient tools for age estimation. Backed by extensive scientific research and cutting-edge technology, our platform is designed to meet the highest standards of accuracy and user experience.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <h2>Contact Us</h2>
        <p>
          Have questions or need support? Reach out to our team, and we'll be happy to assist you.
        </p>
        <form className="contact-form" onSubmit={handleSubmit}>
          <input 
            type="text" 
            name="name"
            placeholder="Your Name" 
            value={formData.name}
            onChange={handleInputChange}
            required 
          />
          <input 
            type="email" 
            name="email"
            placeholder="Your Email" 
            value={formData.email}
            onChange={handleInputChange}
            required 
          />
          <textarea 
            name="message"
            placeholder="Your Message" 
            value={formData.message}
            onChange={handleInputChange}
            required
          ></textarea>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
          {submitStatus.message && (
            <div className={`submit-status ${submitStatus.success ? 'success' : 'error'}`}>
              {submitStatus.message}
            </div>
          )}
        </form>
      </section>

      {/* Footer Section */}
      <footer className="landing-footer">
        <p>&copy; {new Date().getFullYear()} Age Estimation Platform. All rights reserved.</p> <a href="https://ageestimation.netlify.app/privacy" target="_blank" rel="noopener noreferrer">
          Terms and Conditions of Use
        </a>{' '}
      </footer>
    </div>
  );
};

export default LandingPage;
